// Action, label, and asset names/descriptions are user-authored project data
// and untrusted; render as text, never markup. Payload ids are deliberately not
// parsed — the cards are informational and must not leak identifiers.

import { isString } from "../utils/schemaUtils";

export interface ActionScheduleSpec {
  hour: string;
  weekday: string;
  monthDay: string;
  timezone: string;
}

export interface ActionTriggerSpec {
  kind: string;
}

export interface ActionItem {
  name: string;
  description?: string;
  enabled: boolean;
  schedule?: ActionScheduleSpec;
  trigger?: ActionTriggerSpec;
}

export interface ActionListPayload {
  type: "action_list";
  actions: ActionItem[];
}

export interface ActionDetailPayload {
  type: "action_detail";
  action: ActionItem;
}

export interface LabelItem {
  name: string;
  description?: string;
  color?: string;
}

export interface LabelListPayload {
  type: "label_list";
  labels: LabelItem[];
}

export interface AssetItem {
  name: string;
  description?: string;
  labels?: string[];
}

export interface AssetListPayload {
  type: "asset_list";
  assets: AssetItem[];
  hasMore: boolean;
}

const isSchedule = (v: unknown): v is ActionScheduleSpec => {
  if (!v || typeof v !== "object") return false;
  const r = v as Record<string, unknown>;
  return isString(r.hour) && isString(r.weekday) && isString(r.monthDay) && isString(r.timezone);
};

const isTrigger = (v: unknown): v is ActionTriggerSpec => {
  if (!v || typeof v !== "object") return false;
  return isString((v as Record<string, unknown>).kind);
};

const isActionItem = (v: unknown): v is ActionItem => {
  if (!v || typeof v !== "object") return false;
  const r = v as Record<string, unknown>;
  return (
    isString(r.name) &&
    typeof r.enabled === "boolean" &&
    (r.description === undefined || isString(r.description)) &&
    (r.schedule === undefined || isSchedule(r.schedule)) &&
    (r.trigger === undefined || isTrigger(r.trigger))
  );
};

const isLabelItem = (v: unknown): v is LabelItem => {
  if (!v || typeof v !== "object") return false;
  const r = v as Record<string, unknown>;
  return (
    isString(r.name) &&
    (r.description === undefined || isString(r.description)) &&
    (r.color === undefined || isString(r.color))
  );
};

const isAssetItem = (v: unknown): v is AssetItem => {
  if (!v || typeof v !== "object") return false;
  const r = v as Record<string, unknown>;
  return (
    isString(r.name) &&
    (r.description === undefined || isString(r.description)) &&
    (r.labels === undefined || (Array.isArray(r.labels) && r.labels.every(isString)))
  );
};

export const parseActionListPayload = (v: unknown): ActionListPayload | null => {
  if (!v || typeof v !== "object") return null;
  const r = v as Record<string, unknown>;
  if (r.type !== "action_list") return null;
  if (!Array.isArray(r.actions) || !r.actions.every(isActionItem)) return null;
  return { type: "action_list", actions: r.actions };
};

export const parseActionDetailPayload = (v: unknown): ActionDetailPayload | null => {
  if (!v || typeof v !== "object") return null;
  const r = v as Record<string, unknown>;
  if (r.type !== "action_detail") return null;
  if (!isActionItem(r.action)) return null;
  return { type: "action_detail", action: r.action };
};

export const parseLabelListPayload = (v: unknown): LabelListPayload | null => {
  if (!v || typeof v !== "object") return null;
  const r = v as Record<string, unknown>;
  if (r.type !== "label_list") return null;
  if (!Array.isArray(r.labels) || !r.labels.every(isLabelItem)) return null;
  return { type: "label_list", labels: r.labels };
};

export const parseAssetListPayload = (v: unknown): AssetListPayload | null => {
  if (!v || typeof v !== "object") return null;
  const r = v as Record<string, unknown>;
  if (r.type !== "asset_list") return null;
  if (!Array.isArray(r.assets) || !r.assets.every(isAssetItem)) return null;
  return { type: "asset_list", assets: r.assets, hasMore: r.hasMore === true };
};
